import { FC, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Select from 'components/inputs/Select';
import MediaQuery from 'constants/MediaQuery';
import { IMenuItem, isSelected } from '.';

// Under surfplattebredd får sidomenyn inte plats bredvid innehållet och blir en rullgardin ovanför.
const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  border-top: 1px solid ${({ theme }) => theme.colors.background.secondary};

  ${MediaQuery.tablet} {
    grid-template-columns: 220px minmax(0, 1fr);
  }
`;

const SideNav = styled.nav`
  display: none;
  flex-direction: column;
  gap: 2px;
  padding: 16px 0;
  border-right: 1px solid ${({ theme }) => theme.colors.background.secondary};

  ${MediaQuery.tablet} {
    display: flex;
  }
`;

const titleStyle = css`
  font-size: ${({ theme }) => theme.sizes.font.small};
  font-weight: bold;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.foreground.primary};
  opacity: 0.6;
`;

const NavTitle = styled.div`
  ${titleStyle}
  padding: 0 16px 8px;
`;

const NavItem = styled.button<{ $selected: boolean }>`
  padding: 8px 16px 8px 13px;
  border: none;
  border-left: 3px solid transparent;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.foreground.primary};
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.foreground.tint};
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      border-left-color: ${theme.colors.foreground.tint};
      background-color: ${theme.colors.background.secondary};
      font-weight: bold;

      &:hover {
        color: ${theme.colors.foreground.primary};
      }
    `}
`;

const NarrowNav = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 20px 0;

  ${MediaQuery.tablet} {
    display: none;
  }
`;

const NarrowTitle = styled.span`
  ${titleStyle}
`;

// Tabellerna (components/Table) har fasta kolumnbredder; utan egen scroll trycker de ut hela sidan
// i sidled nu när sidomenyn tar 220 px.
const Content = styled.div`
  min-width: 0;
  overflow-x: auto;
`;

/** Rullgardinen visar etiketten som text, så den måste vara en sträng. Underflikarna markeras även på adresser under dem. */
export type ISideSubMenuItem = Omit<IMenuItem, 'label' | 'matchPrefix'> & {
  label: string;
};

type Props = {
  /** Fliken undermenyn hör till, t.ex. "Ruttplanering". Visas ovanför underflikarna. */
  title: string;
  items: ISideSubMenuItem[];
  children: ReactNode;
};

/**
 * Undermeny för en flik som själv ligger i en SubMenu. Underflikarna står i en sidomeny till
 * vänster om innehållet, så att de inte ser ut som en extra rad av menyn ovanför; på smala
 * skärmar blir sidomenyn en rullgardin ovanför innehållet.
 */
const SideSubMenu: FC<Props> = ({ title, items, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const selected = items.find((i) =>
    isSelected(location.pathname, { ...i, matchPrefix: true })
  );

  return (
    <Layout>
      <SideNav aria-label={title}>
        <NavTitle>{title}</NavTitle>
        {items.map((i) => (
          <NavItem
            key={i.path}
            $selected={i === selected}
            aria-current={i === selected ? 'page' : undefined}
            onClick={() => navigate(i.path)}
          >
            {i.label}
          </NavItem>
        ))}
      </SideNav>
      <NarrowNav>
        <NarrowTitle>{title}</NarrowTitle>
        <Select
          value={selected?.path ?? ''}
          onChange={(e) => navigate(e.target.value)}
        >
          {!selected && (
            <option value="" disabled>
              Välj sida
            </option>
          )}
          {items.map((i) => (
            <option key={i.path} value={i.path}>
              {i.label}
            </option>
          ))}
        </Select>
      </NarrowNav>
      <Content>{children}</Content>
    </Layout>
  );
};

export default SideSubMenu;
